"use client";

import { useRouter } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Footer from "@/components/Footer";
import { RotatingTestimonialsSection } from "./RotatingTestimonialsSection";
import {
  PENDING_START_QUERY_PARAM,
  savePendingDriftChallenge,
} from "@/lib/pending-session";
import ScrollyHero from "./ScrollyHero";
import ScrollyDemoSection from "./ScrollyDemoSection";

interface HomeScrollyClientProps {
  isLoggedIn: boolean;
}

const HOLOTYPES = [
  {
    title: "I am not creative",
    description:
      "Good — stay here. Thinknado isn't a creativity tool. It's a thinking one. And you've been doing that your whole life.",
    prompt:
      "I need to come up with a new approach to something at work but I'm not the creative type and I don't really know how to think outside the box.",
  },
  {
    title: "I am an overthinker",
    description:
      "Most people don't think enough. You think too much — and that's the rarer problem to have. Thinknado gives all that horsepower somewhere worth going.",
    prompt:
      "I've been planning this career change for two years and I keep finding reasons it's not the right time.",
  },
  {
    title: "I am the newest person in the room",
    description:
      "Everyone around you seems to already know something you don't. They thought that too. Fresh eyes aren't a disadvantage — they're the rarest thing in any room.",
    prompt:
      "I'm new to my job and I have an idea for how we could do something better but I don't know if I should say anything.",
  },
  {
    title: "I am a realist",
    description:
      "You're not a dreamer. You're practical, grounded, and skeptical of anything that sounds like a brainstorm. Good. Thinknado works better with your instincts in the room.",
    prompt:
      "I need to find a more efficient way to manage my team's workload but every solution I think of creates a new problem.",
  },
  {
    title: "I am a seasoned expert",
    description:
      "Fifteen years of pattern recognition is a superpower. Thinknado introduces patterns your experience hasn't catalogued yet.",
    prompt:
      "Every solution I come up with for this client feels like something I've already done before.",
  },
  {
    title: "I am quietly ambitious",
    description:
      "You have bigger ideas than your current situation suggests. You just haven't found the right thinking partner to help you figure out what to do with them.",
    prompt:
      "I want to do something more meaningful with my career but I don't know how to get from where I am to where I want to be.",
  },
  {
    title: "I am a team lead",
    description:
      "You're already the person who moves things forward. Thinknado is what you bring to the room when forward needs a new direction.",
    prompt:
      "We need a fresh approach to our Q3 campaign and every idea we generate sounds like last year.",
  },
  {
    title: "I am the one with an idea I can't let go of",
    description:
      "You have something brewing. You're not sure if it's good yet. That uncertainty isn't a weakness — it's exactly the right place to start.",
    prompt:
      "I have an idea I believe in but I can't tell if I'm onto something real or just fooling myself.",
  },
  {
    title: "I think in possibilities",
    description:
      "You see where things could go. The hard part is getting everyone else there with you. Thinknado doesn't just follow your thinking — it helps you make it land.",
    prompt:
      "I have a product idea I believe in but I can't find the right way to explain why it matters.",
  },
];

export default function HomeScrollyClient({ isLoggedIn }: HomeScrollyClientProps) {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const continueToSessionWithChallenge = (challenge: string) => {
    const sessionStartTarget = `/app/session?${PENDING_START_QUERY_PARAM}=1`;
    savePendingDriftChallenge(challenge);
    setIsSubmitting(true);
    if (isLoggedIn) {
      router.push(sessionStartTarget);
      return;
    }
    router.push(`/auth/sign-in?redirect=${encodeURIComponent(sessionStartTarget)}`);
  };

  const handleHolotypeStart = (prompt: string) => {
    const challenge = prompt.trim();
    if (!challenge || isSubmitting) return;
    continueToSessionWithChallenge(challenge);
  };

  return (
    <div className="bg-white text-gray-900">
      {/* Hero — observed for sticky CTA trigger */}
      <div ref={heroRef}>
        <ScrollyHero />
      </div>

      {/* Scrollytelling demo */}
      <ScrollyDemoSection isLoggedIn={isLoggedIn} />

      {/* Who Is Thinknado For? */}
      {/* <section
        className="bg-gray-100 border-y border-black/10 py-12 md:py-16 lg:py-32"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.06) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 stack gap-8">
          <div className="stack gap-3 scrolly-reveal">
            <p className="text-[1rem] uppercase tracking-[0.25em] text-gray-500">
              Who Is Thinknado For?
            </p>
            <h2 className="text-black text-left type-4 font-black uppercase text-balance border-b border-black pb-4 md:pb-6">
              What kind of thinker are you?
            </h2>
          </div>

          <div className="grid gap-4 md:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {HOLOTYPES.map((holotype, index) => {
              const staggerClass =
                index % 3 === 0
                  ? "scrolly-reveal"
                  : index % 3 === 1
                  ? "scrolly-stagger-1"
                  : "scrolly-stagger-2";

              return (
                <article
                  key={holotype.title}
                  className={`${staggerClass} border border-black/15 bg-white p-5 stack gap-4 justify-between`}
                >
                  <div className="stack gap-4">
                    <h3 className="text-lg md:text-2xl font-black uppercase leading-tight">
                      {holotype.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      &ldquo;{holotype.description}&rdquo;
                    </p>
                    <div className="border-t border-black/10 pt-4 stack gap-3">
                      <p className="text-sm text-gray-900 leading-relaxed">
                        &ldquo;{holotype.prompt}&rdquo;
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleHolotypeStart(holotype.prompt)}
                    disabled={isSubmitting}
                    className="w-full border border-black/25 px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold text-gray-800 hover:border-black disabled:opacity-40 transition-colors"
                  >
                    {isSubmitting ? "Starting..." : "This Is Me →"}
                  </button>
                </article>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* Bottom CTA band */}
      <section className="bg-white text-black py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="border border-black/20 p-6 md:p-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 scrolly-reveal">
            <div className="stack gap-3 max-w-3xl">
              <p className="text-[1rem] uppercase tracking-[0.25em] text-black/70">
                Try Thinknado Yourself
              </p>
              <h2 className="type-4 font-black uppercase leading-[0.95] text-2xl md:text-4xl">
                Now that you know how to do it, try it yourself
              </h2>
              <p className="text-sm md:text-base text-black/80">
                Bring us whatever&rsquo;s on your mind. Thinknado will take it further than you expected.
              </p>
            </div>
            <Link
              href={isLoggedIn ? "/app/session" : "/auth/sign-in"}
              className="w-full lg:w-auto text-center border border-black bg-black text-white px-6 py-3 text-sm font-black uppercase tracking-[0.14em] hover:bg-white hover:text-black transition-colors"
            >
              Let&rsquo;s Go
            </Link>
          </div>
        </div>
      </section>

      <RotatingTestimonialsSection isLoggedIn={isLoggedIn} />
      <Footer />

      {/* Sticky try-it CTA — appears after hero scrolls out */}
      <AnimatePresence>
        {showStickyCta && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25, ease: [0.215, 0.61, 0.355, 1] }}
            className="fixed bottom-6 right-6 z-50"
          >
            <Link
              href={isLoggedIn ? "/app/session" : "/auth/sign-in"}
              className="inline-flex items-center gap-2 bg-pink-500 text-white px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-pink-600 transition-colors shadow-lg"
            >
              Try It Out
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
